import React, { FC, useEffect, useState } from "react";
import BrandCard from "components/BrandCard/StayCard";
import { DEMO_STAY_LISTINGS } from "data/listings";
import { StayDataType } from "data/types";
import Pagination from "shared/Pagination/Pagination";
import TabFilters from "./TabFilters";
import Heading2 from "components/Heading/Heading2";
import { connect, useDispatch } from "react-redux";
import { updateLoadingIsStatus } from "../../redux/reducers/spinnerReducer";
import api from '../../api';
import { useLocation } from "react-router-dom";
import FilterSection from "components/Search/FilterSection";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { updateKeywords, updateFilter } from "../../redux/reducers/searchReducer";

export interface SectionGridFilterCardProps {
  className?: string;
  data?: StayDataType[];
  keywords: string;
  regions?: string;
  categories?: string;
  user?: any;
  spinner?: any;
  search?: any;
}

const DEMO_DATA: StayDataType[] = DEMO_STAY_LISTINGS.filter((_, i) => i < 8);

let saveTimer = null;
let fetchTimer = null;

const SectionGridFilterCard: FC<SectionGridFilterCardProps> = ({
  className = "",
  data = DEMO_DATA,
  keywords = '',
  regions = '',
  categories = '',
  user,
  spinner,
  search,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const q = queryParams.get('q') ?? null;
  const c = queryParams.get('c') ?? null;

  const [results, setResults] = useState([]);
  const [searchText, setSearchText] = useState(q ? q : search?.keywords ?? '');
  
  // Temporary commented out due to multi fetching bug
  // const [isSearchLoaded, setIsSearchLoaded] = useState(false);

  // useEffect(() => {
  //   if (!isSearchLoaded && user?.id != 0) {
  //     fetchData();
  //     setIsSearchLoaded(true);
  //   }
  // }, [user]);

  useEffect(() => {
    if (fetchTimer) {
      clearTimeout(fetchTimer);
    }

    fetchTimer = setTimeout(() => {
      fetchData();
    }, 10);
    
  }, [search.keywords, search.regions, search.categories])

  const fetchData = () => {
    if (saveTimer) {
      clearTimeout(saveTimer);
    }

    if (fetchTimer) {
      clearTimeout(fetchTimer);
    }

    dispatch(updateLoadingIsStatus(true));
    let params = '';

    params += `q=${searchText}`;

    if (c) params += `&c=${c}`;
    params += `&company_type=${user?.company_type ?? ''}`;

    if (search?.regions?.length > 0) {
      params += `&region_ids=${search?.regions?.join(',') ?? ''}`
    } else {
      params += `&region_ids=${regions ?? ''}`
    }

    if (search?.categories?.length > 0) {
      params += `&category_ids=${search?.categories?.join(',') ?? ''}`
    } else {
      params += `&category_ids=${categories}`
    }

    api.search({
      params
    }, (res) => {
      setResults(res.data);
      dispatch(updateLoadingIsStatus(false));
    });
  }

  const handleKeyUp = (e) => {
    if (saveTimer) {
      clearTimeout(saveTimer);
    }

    if (e.keyCode == 13) {
      handleSearch();
    } else {
      // 3 secs timer for auto search
      saveTimer = setTimeout(() => {
        handleSearch();
      }, 3000);
    }
  }

  const handleSearch = () => {
    if(search?.keywords === searchText){
      fetchData();
    }else{
      dispatch(updateKeywords(searchText));
    }
  }

  const handleClearFilter = () => {
    setSearchText('');
    dispatch(updateKeywords(''));
  }

  return (
    <div className="mt-10 mb-5 w-screen px-[50px] relative left-1/2 -translate-x-1/2 ">
      <div className="flex items-center justify-center">
        <div className="relative w-[1000px]">
          <input
            id={'inpt-search'}
            className={`block w-full bg-transparent border px-4 py-3 pr-12 border-neutral-900 dark:border-neutral-200 rounded-xl focus:ring-0 focus:outline-none text-base leading-none placeholder-neutral-500 dark:placeholder-neutral-300 truncate font-bold placeholder:truncate`}
            placeholder={"What are you looking for?"}
            value={searchText}
            onKeyUp={handleKeyUp}
            onChange={(e) => setSearchText(e.currentTarget.value)}
          />
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2">
            <MagnifyingGlassIcon className="w-5 h-5 text-neutral-700 dark:text-neutral-400" />
          </span>
        </div>
        <button
          className="ml-2 px-4 py-3 rounded-xl bg-neutral-900 text-white dark:bg-neutral-200 dark:text-black"
          onClick={() => handleSearch()}
        >
          Search
        </button>
      </div>

      <div className="flex mt-10 gap-5">
        {/* Left content */}
        <div className="w-[350px]">
          <FilterSection
            handleClearFilter={handleClearFilter}
          />
        </div>
        {/* Right content */}
        <div
          className={`nc-SectionGridFilterCard ${className} !pt-1 flex-1`}
          data-nc-id="SectionGridFilterCard"
        >
          <Heading2 className="!mb-0" heading={`Search results ${search?.keywords?.length ? ' for "' + search.keywords + '"' : ''}`} />
          {/* 
        <div className="mb-8 lg:mb-11">
          <TabFilters />
        </div> */}
          {!spinner && ((search?.regions?.length > 0 && search?.regionsText) || (search?.categories?.length > 0 && search?.categoriesText)) && (
            <div className="p-4">
              <p className="text-lg font-semibold mb-1 text-neutral-800 dark:text-neutral-200">
                Active Filters:
              </p>
              <div className="flex flex-wrap gap-2 ml-1">
                {search?.categories?.length > 0 && search?.categoriesText?.length > 0 && (
                  <span className="inline-flex items-center bg-neutral-200 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-100 px-3 py-1 rounded text-sm font-medium">
                    Category: {search.categoriesText.join(',')}
                  </span>
                )}

                {search?.regions?.length > 0 && search?.regionsText?.length  > 0 && (
                  <span className="inline-flex items-center bg-neutral-200 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-100 px-3 py-1 rounded text-sm font-medium">
                    Regions: {search.regionsText.join(',')}
                  </span>
                )}
              </div>
            </div>
          )}
          {
            spinner ?
              <div className="flex justify-center items-center py-10">
                <div className="w-6 h-6 border-4 border-neutral-300 border-t-neutral-900 rounded-full animate-spin"></div>
              </div>
              : results.length === 0 ? (
                <div className="mt-10 text-center py-10 text-neutral-500 dark:text-neutral-400">
                  No results found.
                </div>
              ) :
                <div className="mt-5 grid grid-cols-1 gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {
                    results.map((brand) => {
                      let brandData = {
                        galleryImgs: brand.images.map((b) => `${api.getUploadUrl()}/${b.url}`),
                        title: brand.name,
                        href: `/${brand.company_type == 'agencies' ? 'agency' : 'brand'}/${brand.shortname}`,
                        like: false,
                        saleOff: false,
                        isAds: false,
                        id: `b-${brand.id}`,
                        extras: brand,
                      };
                      return (
                        <div
                          key={brand.id}
                          className="cursor-pointer hover:scale-[1.02] transition-transform"
                        >
                          <BrandCard key={brand.id} data={brandData} />
                        </div>
                      )
                    })
                  }
                </div>
          }

          {/* <div className="flex mt-16 justify-center items-center">
          <Pagination />
        </div> */}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
  spinner: state.spinner.isLoading,
  search: state.search,
});

export default connect(mapStateToProps, { updateLoadingIsStatus, updateKeywords, updateFilter })(
  SectionGridFilterCard
);
